/*
 * Sticky Add to Cart bar.
 *
 * Deliberately owns no cart logic. It watches the real submit button inside
 * <product-form> and forwards clicks to it, so variant selection, quantity,
 * sold-out state and Dawn's AJAX cart drawer keep working from one place.
 */
class StickyAtc extends HTMLElement {
  connectedCallback() {
    this.targetButton = document.querySelector(this.dataset.target);
    this.submitButton = this.querySelector('[data-sticky-submit]');
    this.priceEl = this.querySelector('[data-sticky-price]');
    this.priceSource = document.querySelector(this.dataset.priceSource);

    if (!this.targetButton || !this.submitButton) return;

    this.submitButton.addEventListener('click', this.forwardClick.bind(this));

    // Show the bar only while the real button is off screen. An observer beats
    // a scroll listener here: no work on frames where nothing crossed.
    this.observer = new IntersectionObserver(
      ([entry]) => this.toggle(!entry.isIntersecting),
      { rootMargin: '0px 0px -80px 0px' }
    );
    this.observer.observe(this.targetButton);

    this.syncState();

    // Dawn re-renders the buy button and the price block on variant change,
    // so both are watched rather than read once at load — otherwise the bar
    // would keep showing a stale price and stay enabled on a sold-out variant.
    this.buttonWatcher = new MutationObserver(this.syncState.bind(this));
    this.buttonWatcher.observe(this.targetButton, {
      attributes: true,
      attributeFilter: ['disabled'],
      childList: true,
      subtree: true,
    });

    if (this.priceSource) {
      this.priceWatcher = new MutationObserver(this.syncPrice.bind(this));
      this.priceWatcher.observe(this.priceSource, { childList: true, subtree: true, characterData: true });
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.buttonWatcher?.disconnect();
    this.priceWatcher?.disconnect();
  }

  toggle(visible) {
    this.classList.toggle('is-visible', visible);
    // Lets the floating WhatsApp button lift clear of the bar instead of
    // sitting behind it.
    document.body.classList.toggle('sticky-atc-visible', visible);
  }

  forwardClick(event) {
    event.preventDefault();
    this.targetButton.click();
  }

  syncState() {
    const disabled = this.targetButton.hasAttribute('disabled');
    this.submitButton.toggleAttribute('disabled', disabled);

    const label = this.targetButton.querySelector('span');
    if (label) this.submitButton.textContent = label.textContent.trim();

    this.syncPrice();
  }

  syncPrice() {
    if (!this.priceSource || !this.priceEl) return;
    // Prefer the sale price when the product is discounted, so the bar shows
    // what the shopper actually pays rather than the compare-at figure.
    const sale = this.priceSource.querySelector('.price__sale .price-item--sale');
    const regular = this.priceSource.querySelector('.price__regular .price-item--regular');
    const source = this.priceSource.classList.contains('price--on-sale') || sale ? sale : regular;
    const text = (source || regular || sale)?.textContent.trim();
    if (text) this.priceEl.textContent = text;
  }
}

if (!customElements.get('sticky-atc')) {
  customElements.define('sticky-atc', StickyAtc);
}
